// client/src/components/chat/RoomList.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tooltip, Badge, Divider, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Link } from 'react-router-dom';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

const STATUS_DOT = { online: '#4CAF50', away: '#FF9800', busy: '#f44336' };

function RoomItem({ room, isActive, unread, onSelect }) {
  return (
    <Box
      onClick={() => onSelect(room.name)}
      sx={{
        px: 1.5, py: 0.75, cursor: 'pointer', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderRadius: 1, mx: 0.5,
        bgcolor: isActive ? 'rgba(124,140,255,0.15)' : 'transparent',
        borderLeft: isActive ? '3px solid #7C8CFF' : '3px solid transparent',
        '&:hover': { bgcolor: isActive ? 'rgba(124,140,255,0.18)' : 'rgba(124,140,255,0.08)' },
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: isActive ? 'primary.main' : 'text.secondary', fontWeight: isActive ? 700 : 400 }}
      >
        # {room.name}
      </Typography>
      {unread > 0 && (
        <Badge badgeContent={unread} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }} />
      )}
    </Box>
  );
}

function FriendEntry({ userId, username, room, status }) {
  const color = STATUS_DOT[status] ?? STATUS_DOT.online;
  return (
    <Tooltip title={`In #${room}`} placement="right">
      <Box
        component={Link}
        to={`/users/${userId}`}
        sx={{
          px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.75,
          textDecoration: 'none', borderRadius: 1, mx: 0.5,
          '&:hover': { bgcolor: 'rgba(124,140,255,0.08)' },
        }}
      >
        <FiberManualRecordIcon sx={{ fontSize: 8, color, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {username}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', ml: 'auto', flexShrink: 0 }}>
          #{room}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function RoomList({ rooms, activeRoom, unreadCounts, onSelectRoom, onSetStatus, onRoomCreated, currentUserId }) {
  const { isFeatureEnabled, featureFlags } = useFeatureFlags();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDisplay, setNewDisplay] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [friendsOnline, setFriendsOnline] = useState([]);

  const defaultRooms = rooms.filter(r => r.isDefault);
  const userRooms = rooms.filter(r => !r.isDefault);

  useEffect(() => {
    if (!featureFlags?.enableFollowSystem || !featureFlags?.enableChatRooms) return;
    Promise.all([
      axiosInstance.get('/follow/following'),
      axiosInstance.get('/chat/online'),
    ]).then(([followRes, onlineRes]) => {
      const followingIds = new Set((followRes.data || []).map(u => String(u._id)));
      const online = (onlineRes.data || []).filter(u => followingIds.has(String(u.userId)));
      setFriendsOnline(online);
    }).catch((err) => {
      console.warn('Failed to load friends online:', err);
    });
  }, [featureFlags?.enableFollowSystem, featureFlags?.enableChatRooms]);

  const handleCreate = async () => {
    setError('');
    setCreating(true);
    try {
      const res = await axiosInstance.post('/chat/rooms', { name: newName, displayName: newDisplay });
      setCreateOpen(false);
      setNewName('');
      setNewDisplay('');
      if (onRoomCreated) onRoomCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create room');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{
      width: 200, flexShrink: 0,
      bgcolor: 'rgba(0,0,0,0.25)',
      borderRight: '1px solid rgba(255,255,255,0.08)',
      overflowY: 'auto', display: 'flex', flexDirection: 'column',
    }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1.5 }}>
          ROOMS
        </Typography>
      </Box>

      {defaultRooms.map(room => (
        <RoomItem key={room.name} room={room} isActive={activeRoom === room.name}
          unread={unreadCounts[room.name] || 0} onSelect={onSelectRoom} />
      ))}

      {userRooms.length > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" sx={{ px: 1.5, color: 'text.disabled' }}>USER ROOMS</Typography>
          {userRooms.map(room => (
            <RoomItem key={room.name} room={room} isActive={activeRoom === room.name}
              unread={unreadCounts[room.name] || 0} onSelect={onSelectRoom} />
          ))}
        </>
      )}

      {friendsOnline.length > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" sx={{ px: 1.5, color: 'text.disabled' }}>FRIENDS ONLINE</Typography>
          {friendsOnline.map(f => (
            <FriendEntry key={f.userId} userId={f.userId} username={f.username} room={f.room} status={f.status} />
          ))}
        </>
      )}

      <Box sx={{ mt: 'auto', p: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Tooltip title="Create a room">
          <Button
            size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ color: 'text.disabled', fontSize: 11, width: '100%', justifyContent: 'flex-start',
              '&:hover': { color: 'primary.main' } }}
          >
            Create room
          </Button>
        </Tooltip>
      </Box>

      <Box sx={{ p: 1, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 0.5 }}>
        {['online', 'away', 'busy'].map(s => (
          <Tooltip key={s} title={s.charAt(0).toUpperCase() + s.slice(1)}>
            <Box
              onClick={() => onSetStatus(s, '')}
              sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: STATUS_DOT[s], cursor: 'pointer',
                border: '2px solid rgba(255,255,255,0.2)', '&:hover': { opacity: 0.8 } }}
            />
          </Tooltip>
        ))}
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Create a room</DialogTitle>
        <DialogContent>
          <TextField
            label="Room name (e.g. snes-games)" value={newName}
            onChange={e => setNewName(e.target.value)}
            size="small" fullWidth sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            label="Display name (e.g. SNES Games)" value={newDisplay}
            onChange={e => setNewDisplay(e.target.value)}
            size="small" fullWidth
          />
          {error && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating || !newName || !newDisplay} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
